import { useCallback, useRef, useState } from 'react';

/** What the hook feeds into the panel component — spread at the call site. */
export interface SidebarControlProps {
  visible: boolean;
  /** Close without saving. */
  onHide: () => void;
  /** Saved successfully — the owning page reloads its data. */
  onComplete: () => void;
}

/**
 * Owns a side panel's open state and keeps its component's identity stable.
 *
 * A panel component declared *inside* its `useCreateXSidebar` hook gets a new function identity on
 * every render of the owning page. React compares element types by reference, so that reads as a
 * different component: it unmounts the open panel and mounts a fresh one, throwing away the form
 * and re-running every fetch. On a scroll-spy page (Tabs calls onTabChange as sections pass under
 * the strip) that happens while the user scrolls, which is what made open panels "reload".
 *
 * The fix is to declare the component at module scope and let it take `visible`/`onHide` as props.
 * `mountKey` then restores the one thing the remount was accidentally providing: useForm seeds its
 * state at mount and never re-syncs with `initialData`, so a panel that is never remounted would
 * reopen showing the values from the previous edit session. The key changes when the panel opens —
 * never while it is open — so the content rebuilds from current data on open and is left alone
 * during editing.
 */
export const useSidebarControl = (onComplete?: () => void) => {
  const [visible, setVisibleState] = useState(false);
  const [mountKey, setMountKey] = useState(0);

  // Callers pass a fresh arrow on every render; the ref keeps `complete` (and so `sidebarProps`)
  // from churning with it.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const setVisible = useCallback((v: boolean) => {
    if (v) setMountKey(k => k + 1);
    setVisibleState(v);
  }, []);

  // Closing deliberately leaves mountKey alone: no remount on the way out, so the panel keeps its
  // content through the slide-out animation.
  const onHide = useCallback(() => setVisibleState(false), []);
  const onCompleteStable = useCallback(() => onCompleteRef.current?.(), []);

  const sidebarProps: SidebarControlProps = { visible, onHide, onComplete: onCompleteStable };

  return { visible, setVisible, mountKey, sidebarProps };
};

export default useSidebarControl;
